/**
 * The single place anything talks to the API.
 *
 * Shared by the dispatcher console and the mobile apps, because the three things this
 * owns are the three that are easy to get subtly wrong in isolation: attaching the access
 * token, attaching the tenant, and refreshing an expired token without the caller
 * noticing. Two implementations of that would drift, and the way they would drift is
 * silent — a second app that reuses a rotated refresh token looks fine until it signs
 * someone out mid-shift.
 *
 * Nothing platform-specific lives here. The base URL and where the refresh token is kept
 * are supplied by whoever configures it: the console reads a Vite variable and keeps the
 * refresh token in one `localStorage` slot its tabs share, while the driver app has a real
 * secure store and a session that must survive the app being killed.
 */

import type { AppProblem } from './problem';
import { toProblem } from './problem';
import type { Schema } from './schemas';

export type TokenPair = Schema<'TokenPair'>;

/**
 * Where the refresh token is kept between launches.
 *
 * A browser has nowhere safe — anything in `localStorage` is readable by any script that
 * manages to run on the page, so one cross-site scripting bug hands over a dispatcher's
 * session. A phone has a real keychain. The difference is the platform's, not this
 * module's, so it is injected rather than decided here.
 */
export interface TokenStore {
  read(): Promise<string | null>;
  write(refreshToken: string | null): Promise<void>;

  /**
   * For a store that other holders write to as well — a browser's tabs share one.
   *
   * Runs a refresh while no other holder is refreshing, and marks the store as the one
   * that knows the current token: inside it the refresh presents what `read` returns
   * rather than the token this holder last saw, because another tab may have rotated it
   * since. Two tabs presenting the same token is exactly what the server reads as theft,
   * and it answers by revoking the session in every tab at once. A `read` of nothing there
   * means this holder's session is over, and it ends without asking the server.
   *
   * Absent on a store only one holder uses, such as a phone's keychain.
   */
  exclusive?<T>(work: () => Promise<T>): Promise<T>;
}

/** Keeps nothing, so the session dies with the process. The default, and what the tests use. */
export const noTokenStore: TokenStore = {
  read: () => Promise.resolve(null),
  write: () => Promise.resolve(),
};

export interface ApiConfiguration {
  /** Where the API lives. Empty for a same-origin deployment. */
  baseUrl: string;
  tokens?: TokenStore;
}

let baseUrl = '';
let store: TokenStore = noTokenStore;

export function configure(configuration: ApiConfiguration): void {
  baseUrl = configuration.baseUrl;
  store = configuration.tokens ?? noTokenStore;
}

/**
 * Access tokens are held in memory, never persisted.
 *
 * Only the refresh token reaches the store, and only where the platform has somewhere
 * safe to put it. An access token is short-lived and replaceable; writing it down buys
 * nothing and widens what a compromise yields.
 */
let tokens: TokenPair | null = null;
let tenantSlug: string | null = null;

/**
 * When the access token stops working, by this device's clock.
 *
 * Worked out from the token's own lifetime rather than read off `expiresAt`, which is the
 * server's clock: a workstation set a quarter of an hour wrong would otherwise refresh on
 * every call or never, and never is a hub that reconnects with a dead token.
 */
let accessDeadline = 0;

type SessionEndedListener = () => void;
const sessionEndedListeners = new Set<SessionEndedListener>();

/**
 * Called when the session ends by itself — the server refused to refresh it.
 *
 * Not when the app ends it with `setTokens(null)`: somebody who pressed *Sign out* knows,
 * and telling them their session expired would be telling them something false.
 */
export function onSessionEnded(listener: SessionEndedListener): () => void {
  sessionEndedListeners.add(listener);
  return () => sessionEndedListeners.delete(listener);
}

export function setTokens(next: TokenPair | null): void {
  tokens = next;
  accessDeadline = next === null ? 0 : deadlineOf(next);

  void store.write(next?.refreshToken ?? null);
}

function endSession(): void {
  setTokens(null);

  for (const listener of sessionEndedListeners) listener();
}

export function getAccessToken(): string | null {
  return tokens?.accessToken ?? null;
}

/**
 * How long before its deadline a token counts as spent.
 *
 * A minute covers the server's thirty seconds of clock skew with room for a request that
 * is still on the wire when the token lapses.
 */
const EARLY_MS = 60_000;

/**
 * An access token that will still be accepted, refreshing first when this one is about
 * to lapse. Null when there is no session.
 *
 * For a caller that cannot ride the 401-and-retry in {@link request}. A SignalR connection
 * is the one that matters: it presents its token once, when it connects, and a refusal
 * there is not retried with a new one — so a hub that reconnects after a dropped socket
 * with the token from twenty minutes ago is refused, runs out of retries, and stops
 * delivering anything, with nothing on the screen to say so. Also the console's periodic
 * check, which is how a session revoked on an idle screen is noticed at all.
 */
export async function freshAccessToken(): Promise<string | null> {
  if (tokens === null) return null;

  if (Date.now() >= accessDeadline - EARLY_MS) await refreshTokens();

  return getAccessToken();
}

/**
 * Reads the lifetime out of the token and starts it from now.
 *
 * `exp − nbf` is a span measured on one clock, the server's, so it is right here whatever
 * this clock says. A token that cannot be read counts as already spent, which costs one
 * refresh rather than a hub that cannot connect.
 */
function deadlineOf(pair: TokenPair): number {
  try {
    const payload = pair.accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { nbf, exp } = JSON.parse(atob(payload)) as { nbf?: number; exp?: number };

    if (typeof nbf === 'number' && typeof exp === 'number') return Date.now() + (exp - nbf) * 1000;
  } catch {
    // Falls through: an empty placeholder while a stored session is restored, or a token
    // shaped differently from ours.
  }

  return 0;
}

export function setTenant(slug: string | null): void {
  tenantSlug = slug;
}

export function getTenant(): string | null {
  return tenantSlug;
}

/**
 * Where a spent access token is exchanged for a new one.
 *
 * Almost always the tenant door, which is why that is the default. A platform session
 * refreshes at `/api/platform/refresh` instead — it belongs to no firm, and the backend
 * refuses to trade a session scoped to nobody for one scoped to somebody, so presenting a
 * platform token at the tenant door is an unrecoverable 401 rather than a redirect. Left
 * hardcoded, that would sign a platform operator out fifteen minutes into a provisioning
 * session with nothing on screen to explain why.
 */
let refreshPath = '/api/auth/refresh';

export function setRefreshPath(path: string): void {
  refreshPath = path;
}

/**
 * What became of a stored session on launch.
 *
 * - `signed-in` — the server took the refresh token and the session is live.
 * - `signed-out` — there was none, or the server refused it, which ended it.
 * - `offline` — there is one, and the server could not be asked: no signal, or the API
 *   restarting at a deploy. It is still held, and the first request that reaches the
 *   server renews it.
 *
 * The last two used to be one answer, `false`, and both apps showed the sign-in screen for
 * it — so a passenger opening the app in a lift was asked for an SMS code with a perfectly
 * good session in the keychain.
 */
export type Restored = 'signed-in' | 'signed-out' | 'offline';

/**
 * Restores a session from a persisted refresh token.
 *
 * For the two apps, which the OS kills and reopens all day, and the console after F5.
 */
export async function restore(): Promise<Restored> {
  const refreshToken = await store.read();

  if (refreshToken === null) return 'signed-out';

  // Spent as well as empty, so a connection asking for a token meanwhile waits for the
  // refresh below rather than being handed nothing. Restoring again over a live session —
  // what Fast Refresh does — used to leave the old deadline standing, and the hub connected
  // with an empty token: a 401 SignalR reports as "the connection could not be found".
  tokens = { accessToken: '', refreshToken, expiresAt: new Date(0).toISOString() };
  accessDeadline = 0;

  if (await refreshTokens()) return 'signed-in';

  // A refusal has already cleared the session; anything else kept it.
  return tokens === null ? 'signed-out' : 'offline';
}

/**
 * A single in-flight refresh, shared by every request that needs one.
 *
 * Without this, a client that fires eight requests on load would send eight refreshes
 * with the same token. Refresh tokens rotate and reuse revokes the whole family, so
 * seven of those would be treated as theft and sign the user out — a bug that only
 * appears under exactly the concurrency a real screen produces.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  if (refreshInFlight !== null) return refreshInFlight;

  const shared = store.exclusive?.bind(store);

  const attempt = async (): Promise<boolean> => {
    if (tokens === null) return false;

    let presented = tokens.refreshToken;

    // Inside the lock, a shared store knows better than this holder does: another tab may
    // have rotated the token a minute ago, and the copy in memory is now a used one.
    if (shared !== undefined) {
      const latest = await store.read();

      // Nothing there for this session: another tab signed out, or somebody else signed in
      // and the browser is theirs now. The copy in memory is spent or revoked either way,
      // and presenting it would be logged at the server as a stolen token being replayed.
      if (latest === null) {
        endSession();
        return false;
      }

      presented = latest;
    }

    try {
      // Bounded like every other request. A refresh that hangs blocks every caller waiting
      // on refreshInFlight, so this is the one worth timing out most.
      const response = await fetchWithTimeout(`${baseUrl}${refreshPath}`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ refreshToken: presented }),
      });

      // Only a refusal ends the session. A 502 from a proxy while the API restarts, or a
      // 429, is the server being unavailable rather than the server saying no — and ending
      // the session over it signs every dispatcher out at every deploy.
      if (REFUSED.has(response.status)) {
        endSession();
        return false;
      }

      if (!response.ok) return false;

      setTokens((await response.json()) as TokenPair);
      return true;
    } catch {
      // A network failure is not proof the session is invalid, so the tokens are kept
      // and the caller sees the original error. This matters far more on a phone than
      // in an office: a driver goes through tunnels and underground car parks all day,
      // and being signed out by a dead spot would be unusable.
      return false;
    }
  };

  refreshInFlight = (shared === undefined ? attempt() : shared(attempt)).finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

/**
 * The answers that mean the refresh token will never be accepted again.
 *
 * The endpoint says 401 for all four of its refusals — unknown, lapsed, reused, another
 * firm's; 400 is a request it could not read, and 403 a door this session may not use.
 */
const REFUSED = new Set([400, 401, 403]);

function jsonHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  // The tenant header only matters before sign-in; afterwards the token claim wins and
  // the server ignores it. Sending it anyway keeps sign-in and refresh working on a
  // shared host name.
  if (tenantSlug !== null) headers['X-Tenant'] = tenantSlug;
  if (tokens !== null && tokens.accessToken.length > 0) {
    headers.Authorization = `Bearer ${tokens.accessToken}`;
  }

  return headers;
}

export class ApiError extends Error {
  // Declared rather than written as constructor parameter properties: the toolchain runs
  // with erasableSyntaxOnly, which only permits TypeScript that can be stripped without
  // emitting any runtime code.
  readonly problem: AppProblem;
  readonly status: number;

  constructor(problem: AppProblem, status: number) {
    super(problem.code);
    this.name = 'ApiError';
    this.problem = problem;
    this.status = status;
  }
}

/**
 * The session a sign-in issued.
 *
 * A sign-in answers with a session or, for an account with a second factor enrolled, a
 * challenge and no tokens. The console and the ops site ask for the code; the two apps
 * cannot, and handing the empty answer to `setTokens` signed them in to nothing. Refused
 * here instead, so the app's ordinary "could not sign in" is what the person sees.
 */
export function sessionOf(result: Schema<'AuthResult'>): TokenPair {
  const { accessToken, refreshToken, expiresAt } = result;

  if (accessToken === null || refreshToken === null || expiresAt === null) {
    throw new ApiError(
      { code: 'request.failed', status: 401, title: 'A second factor is required, which this app cannot ask for.' },
      401,
    );
  }

  return { accessToken, refreshToken, expiresAt };
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Internal: stops a refreshed request from refreshing again and looping. */
  retrying?: boolean;
  /** Longer than {@link TIMEOUT_MS} for the few requests that work through a whole file — {@link LONG_TIMEOUT_MS}. */
  timeoutMs?: number;
}

/**
 * How long any one request may hang before it is treated as offline.
 *
 * Without this a request to an unroutable address never settles: the platform waits out its
 * own TCP timeout, which on Android is minutes, and the caller's spinner runs the whole
 * time. That is not a hypothetical — it is what a phone does with a wrong API address, and
 * what a driver's phone does in a tunnel or a car park.
 *
 * Fifteen seconds is chosen against the slowest thing here rather than the average. A cold
 * dispatch query on a loaded server is comfortably inside it, and a driver deciding whether
 * to accept a job has thirty seconds in total, so a request still running at fifteen has
 * already failed them whatever the network eventually says.
 */
const TIMEOUT_MS = 15_000;

/**
 * How long an import may take: a firm's whole list of callers or places, read, saved and
 * linked. Fifteen seconds is right for a phone and wrong for that — BulgariaTaxi's sixteen
 * thousand addresses took fifteen on the production server, and the console gave up a moment
 * before the server finished, so the screen said the file could not be read when it had been.
 * Only the console's import screens ask for it.
 */
export const LONG_TIMEOUT_MS = 300_000;

async function fetchWithTimeout(url: string, init: RequestInit, timeoutMs = TIMEOUT_MS): Promise<Response> {
  // AbortSignal.timeout would be tidier, but React Native's implementation has lagged and
  // this works everywhere. The clear runs in a finally so a fast response does not leave a
  // timer holding the event loop open.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, retrying = false, timeoutMs } = options;

  const response = await fetchWithTimeout(
    `${baseUrl}${path}`,
    {
      method,
      headers: jsonHeaders(),
      body: body === undefined ? undefined : JSON.stringify(body),
    },
    timeoutMs,
  );

  // One retry, and only once. An access token lives about fifteen minutes, so an expiry
  // mid-session is ordinary rather than exceptional, and nobody should ever see it.
  if (response.status === 401 && !retrying && tokens !== null) {
    const refreshed = await refreshTokens();
    if (refreshed) return request<T>(path, { ...options, retrying: true });

    // Still held means the refresh never reached a server that could say no — no signal,
    // or the API restarting — and the session is fine. Saying it expired would be false,
    // and on a phone it reads as an instruction to sign in again. This fails the way a
    // request with no signal fails, which every screen already words as being offline.
    if (tokens !== null) throw new Error('The session could not be renewed: the server was not reached.');

    throw new ApiError(
      { code: 'auth.session_expired', status: 401, title: 'Session expired' },
      401,
    );
  }

  if (!response.ok) throw new ApiError(await toProblem(response), response.status);

  return (await bodyOf(response)) as T;
}

/**
 * The body, or nothing when there is none. Several endpoints answer with no body on
 * purpose: 204 for a customer lookup that found nobody, 202 for a one-time code that has
 * been accepted for sending. Calling `response.json()` on an empty body throws a
 * SyntaxError — not an ApiError — and every screen renders an error it does not
 * recognise as "no connection", which is how a working sign-in looked broken.
 */
async function bodyOf(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;

  const text = await response.text();

  return text === '' ? undefined : JSON.parse(text);
}

/**
 * A file sent to the API, with the caller's credentials.
 *
 * The counterpart to {@link download}, and separate from {@link request} for a specific
 * reason: the body is `FormData`, and the `Content-Type` header must be *omitted* so the
 * browser can set it itself. A multipart content type without the boundary the browser
 * generated is unparseable at the other end, which presents as an empty file rather than as
 * an error anybody can act on.
 */
export async function upload<T>(
  path: string,
  file: Blob,
  field = 'file',
  filename = 'upload',
  timeoutMs?: number,
): Promise<T> {
  const form = new FormData();
  form.append(field, file, filename);

  const headers = jsonHeaders();
  delete headers['Content-Type'];

  const response = await fetchWithTimeout(
    `${baseUrl}${path}`,
    {
      method: 'POST',
      headers,
      body: form,
    },
    timeoutMs,
  );

  // The same single retry as everywhere else: a token expiring mid-session is ordinary.
  if (response.status === 401 && tokens !== null && (await refreshTokens())) {
    return upload<T>(path, file, field, filename);
  }

  if (!response.ok) throw new ApiError(await toProblem(response), response.status);

  return (await bodyOf(response)) as T;
}

/**
 * A file the API generated, fetched with the caller's credentials.
 *
 * A plain `<a href>` to an export endpoint carries no bearer token and quietly returns a
 * 401 — which presents as a downloaded file containing a JSON error, opened in a
 * spreadsheet, in front of whoever asked for it. So the file comes back through the
 * authenticated client and is handed to the browser as a blob.
 *
 * The filename is taken from `Content-Disposition` where the server sent one, so the report
 * names its own file and the console does not have to keep a second copy of that rule.
 */
export async function download(
  path: string,
): Promise<{ blob: Blob; filename: string | null }> {
  const response = await fetchWithTimeout(`${baseUrl}${path}`, {
    method: 'GET',
    headers: jsonHeaders(),
  });

  // The same one retry as request(), and for the same reason: an access token lives about
  // fifteen minutes, so an expiry mid-session is ordinary and nobody should ever see it.
  if (response.status === 401 && tokens !== null && (await refreshTokens())) {
    return download(path);
  }

  if (!response.ok) throw new ApiError(await toProblem(response), response.status);

  return {
    blob: await response.blob(),
    filename: filenameFrom(response.headers.get('Content-Disposition')),
  };
}

/** The name the server gave the file, or nothing. */
function filenameFrom(disposition: string | null): string | null {
  if (disposition === null) return null;

  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);

  return match === null ? null : decodeURIComponent(match[1]);
}
